<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :fullscreen="isMobile"
    :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'"
    max-width="520"
    scrollable
  >
    <v-card class="d-flex flex-column" :height="isMobile ? '100%' : 600">
      <v-toolbar flat dense class="flex-grow-0">
        <v-btn v-if="stack.length && !buscando" icon @click="volver" aria-label="Volver">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="text-subtitle-1">{{ titulo }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="cerrar" aria-label="Cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div class="px-4 pt-2 pb-1 flex-grow-0">
        <v-text-field
          v-model="busqueda"
          placeholder="Buscar categoría"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          hide-details
        />
        <div v-if="stack.length && !buscando" class="ruta mt-2">
          <a class="ruta-link" @click="irA(0)">Todas</a>
          <template v-for="(nodo, i) in stack">
            <v-icon :key="'s' + nodo.id" x-small class="mx-1">mdi-chevron-right</v-icon>
            <a
              v-if="i < stack.length - 1"
              :key="nodo.id"
              class="ruta-link"
              @click="irA(i + 1)"
            >{{ nodo.name }}</a>
            <span v-else :key="nodo.id" class="font-weight-medium">{{ nodo.name }}</span>
          </template>
        </div>
      </div>

      <v-divider />

      <v-card-text class="pa-0 flex-grow-1">
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="error" class="text-center pa-8">
          <p class="mb-4">{{ error }}</p>
          <v-btn outlined @click="cargar">Reintentar</v-btn>
        </div>

        <!-- Resultados de búsqueda: lista plana con la ruta de cada categoría -->
        <v-list v-else-if="buscando" class="py-0">
          <v-list-item
            v-for="item in resultados"
            :key="item.nodo.id"
            @click="elegirResultado(item)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ item.nodo.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.ruta.length">
                {{ item.ruta.map((n) => n.name).join(" › ") }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon v-if="esSeleccionada(item.nodo)" color="primary">mdi-check</v-icon>
              <v-icon v-else-if="soloHojas && tieneHijos(item.nodo)">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <div v-if="!resultados.length" class="text-center pa-8 grey--text">
            No hay categorías que coincidan con "{{ busqueda }}"
          </div>
        </v-list>

        <!-- Navegación por niveles -->
        <v-list v-else class="py-0">
          <v-list-item v-if="!soloHojas && actual" @click="elegir(actual)">
            <v-list-item-icon class="mr-4">
              <v-icon>mdi-folder-check-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Todo en {{ actual.name }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="esSeleccionada(actual)">
              <v-icon color="primary">mdi-check</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="!soloHojas && actual" />

          <v-list-item v-for="nodo in hijosActuales" :key="nodo.id" @click="tocar(nodo)">
            <v-list-item-content>
              <v-list-item-title>{{ nodo.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="tieneHijos(nodo)">
                {{ nodo.children.length }}
                {{ nodo.children.length === 1 ? "subcategoría" : "subcategorías" }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon v-if="esSeleccionada(nodo)" color="primary">mdi-check</v-icon>
              <v-icon v-else-if="tieneHijos(nodo)">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <div v-if="!hijosActuales.length" class="text-center pa-8 grey--text">
            No hay categorías cargadas
          </div>
        </v-list>
      </v-card-text>

      <v-divider v-if="soloHojas" />
      <div v-if="soloHojas" class="px-4 py-2 caption grey--text flex-grow-0">
        Solo se pueden elegir categorías del último nivel.
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import CategoriasAPI from "@/api/categorias";

const normalizar = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Quita "SIN CATEGORIA" del árbol (el backend la excluye en los listados por nivel).
const limpiar = (nodo) => ({
  ...nodo,
  children: (nodo.children || [])
    .filter((c) => c.name !== "SIN CATEGORIA")
    .map(limpiar),
});

export default {
  name: "CategoriaPicker",
  props: {
    // v-model: dialog abierto/cerrado
    value: { type: Boolean, default: false },
    // true: solo se eligen hojas (asignar categoría a producto)
    // false: se elige cualquier nivel (filtros)
    soloHojas: { type: Boolean, default: true },
    seleccionada: { type: Object, default: null },
  },
  data() {
    return {
      raiz: null,
      stack: [],
      busqueda: "",
      loading: false,
      error: null,
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    actual() {
      return this.stack.length ? this.stack[this.stack.length - 1] : null;
    },
    hijosActuales() {
      const nodo = this.actual || this.raiz;
      return nodo ? nodo.children : [];
    },
    titulo() {
      if (this.buscando) return "Buscar categoría";
      return this.actual ? this.actual.name : "Elegí una categoría";
    },
    buscando() {
      return normalizar(this.busqueda).trim().length > 0;
    },
    // Lista plana { nodo, ruta } para la búsqueda
    indice() {
      const out = [];
      const recorrer = (nodos, ruta) => {
        nodos.forEach((nodo) => {
          out.push({ nodo, ruta, texto: normalizar(nodo.name) });
          if (nodo.children.length) recorrer(nodo.children, [...ruta, nodo]);
        });
      };
      if (this.raiz) recorrer(this.raiz.children, []);
      return out;
    },
    resultados() {
      const q = normalizar(this.busqueda).trim();
      return this.indice.filter((i) => i.texto.includes(q)).slice(0, 100);
    },
  },
  watch: {
    value: {
      handler(abierto) {
        if (!abierto) return;
        this.busqueda = "";
        if (!this.raiz) this.cargar();
        else this.posicionarEnSeleccionada();
      },
      immediate: true,
    },
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await CategoriasAPI.indexFilters();
        this.raiz = limpiar(data);
        this.posicionarEnSeleccionada();
      } catch (e) {
        this.error = "No se pudieron cargar las categorías.";
      } finally {
        this.loading = false;
      }
    },
    // Al abrir, mostrar el nivel donde está la categoría ya elegida
    posicionarEnSeleccionada() {
      this.stack = [];
      if (!this.seleccionada) return;
      const item = this.indice.find((i) => this.esSeleccionada(i.nodo));
      if (!item) return;
      this.stack = this.soloHojas || !this.tieneHijos(item.nodo) ? [...item.ruta] : [...item.ruta, item.nodo];
    },
    tieneHijos(nodo) {
      return nodo.children && nodo.children.length > 0;
    },
    esSeleccionada(nodo) {
      const sel = this.seleccionada;
      if (!sel) return false;
      if (sel.id != null) return sel.id === nodo.id;
      return !!sel.name && normalizar(sel.name).replace(/-/g, " ") === normalizar(nodo.name);
    },
    tocar(nodo) {
      if (this.tieneHijos(nodo)) this.stack.push(nodo);
      else this.elegir(nodo);
    },
    elegirResultado(item) {
      if (this.soloHojas && this.tieneHijos(item.nodo)) {
        this.stack = [...item.ruta, item.nodo];
        this.busqueda = "";
      } else {
        this.elegir(item.nodo);
      }
    },
    elegir(nodo) {
      this.$emit("select", {
        id: nodo.id,
        name: nodo.name,
        mptt_level: nodo.mptt_level,
        tiene_hijos: this.tieneHijos(nodo),
      });
      this.cerrar();
    },
    volver() {
      this.stack.pop();
    },
    irA(n) {
      this.stack = this.stack.slice(0, n);
    },
    cerrar() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.ruta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 13px;
  line-height: 1.8;
}
.ruta-link {
  cursor: pointer;
  text-decoration: underline;
  color: inherit;
  opacity: 0.8;
}
</style>
